import { Stack } from '@fluentui/react';
import { Button, Checkbox } from '@h2oai/ui-kit';
import { memo, useCallback, useMemo } from 'react';

import type { Tag } from '../../../../authz/gen/ai/h2o/workspace/v1/tag_pb';
import { PageFilters } from '../../../../components/PageFilters/PageFilters';
import type { FilterState, PageFiltersElement } from '../../../../components/PageFilters/types';
import { createWorkspaceFiltersConfig } from '../WorkspacePageFilters/workspaceFiltersConfig';

export type WorkspacePageControlsProps = {
  filters: Record<string, FilterState<unknown>>;
  onFiltersChange: (newFilters: Record<string, FilterState<unknown>>) => void;
  onClearFilters: () => void;
  availableTags?: Tag[];
  showDeleted: boolean;
  onToggleShowDeleted: () => void;
};

export const WorkspacePageControls = memo((props: WorkspacePageControlsProps) => {
  const { filters, onFiltersChange, onClearFilters, availableTags, showDeleted, onToggleShowDeleted } = props;

  const filtersConfig = useMemo(() => {
    const baseConfig = createWorkspaceFiltersConfig(availableTags);

    return baseConfig.map((filter) => {
      const currentFilterState = filters[filter.key];

      return {
        ...filter,
        state: {
          value: currentFilterState?.value ?? filter?.state?.value,
          readableValue: currentFilterState?.readableValue ?? filter?.state?.readableValue,
        },
      };
    }) as PageFiltersElement[];
  }, [availableTags, filters]);

  const handleClearFilter = useCallback(
    (filterKey: string) => {
      const newFilters = { ...filters };
      delete newFilters[filterKey];
      onFiltersChange(newFilters);
    },
    [filters, onFiltersChange]
  );

  return (
    <Stack tokens={{ childrenGap: 8 }}>
      <PageFilters
        filters={filtersConfig}
        filterValues={filters}
        onFiltersChange={onFiltersChange}
        onClearFilters={onClearFilters}
        onClearFilter={handleClearFilter}
      />
      <Stack>
        <Button onClick={onToggleShowDeleted}>
          <Checkbox
            id="toggle-show-deleted"
            key="toggle-show-deleted"
            styles={{ root: { pointerEvents: 'none' } }}
            checked={showDeleted}
          />
          Include Deleted and Archived
        </Button>
      </Stack>
    </Stack>
  );
});
